import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Table, Tab } from 'rbx';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useStoreState } from 'easy-peasy';
import { isNull, uniq } from 'lodash';
import { toast } from 'react-toastify';
import uuidv4 from 'uuid/v4';
import swal from 'sweetalert';

import { Table as CustomTable, Box, Tabs } from '../../../components/elements';

import Header from './Header';
import List from './List';
import AddEditForm from './AddEditForm';
import client from '../../../utils/apolloClient';

const Container = styled.div`
  .table-container {
    min-height: 30rem;
  }
`;

const createFeeMutation = gql`
  mutation createFee($input: OrderFeeDefaultInput) {
    create_order_fee_default(input: $input) {
      fee_id
      is_payment_gateway_fee
      error {
        description
      }
    }
  }
`;

const updateFeeMutation = gql`
  mutation updateFee($input: OrderFeeDefaultInput) {
    update_order_fee_default(input: $input) {
      fee_id
      error {
        description
      }
    }
  }
`;
const usersQuery = gql`
  query searchUsers($input: SearchInput) {
    search_users(input: $input) {
      user_listing {
        user_id
        display_name
        first_name
        last_name
        full_name
        email
      }
    }
  }
`;

const deleteFeeMutation = gql`
  mutation deleteFee($input: OrderFeeDefaultInput) {
    delete_order_fee_default(input: $input) {
      fee_id
      error {
        description
      }
    }
  }
`;
const Details = ({ fees: feeValues, refetch, taxesOptions }) => {
  const { userId } = useStoreState(state => state.auth);
  const [fees, setFees] = useState([]);
  const [fee, setFee] = useState(null);
  const [users, setUsers] = useState([]);
  const [isActiveModal, setIsActiveModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Pickup');

  useEffect(() => {
    const data = feeValues
      .map(item => ({
        fee_id: item.order_fee.fee_id,
        service_type_id: item.service_type_id,
        service_type_name: item.name,
        name: item.order_fee.name,
        display: item.order_fee.display,
        sale_method: item.order_fee.sale_method,
        applies_to: item.order_fee.applies_to,
        type: item.order_fee.type,
        value: item.order_fee.value,
        status: item.order_fee.status,
        is_payment_gateway_fee: item.order_fee.is_payment_gateway_fee,
        is_kravein_charge: item.order_fee.is_kravein_charge,
        tax: item.order_fee.tax,
        taxValue: item.order_fee.tax_value,
        min_fee: item.order_fee.min_fee,
        max_fee: item.order_fee.max_fee,
        audit: item.order_fee.audit,
      }))
      .filter(item => item.service_type_name === activeTab);
    setFees(data);
  }, [feeValues, activeTab]);

  useEffect(() => {
    const userIds = uniq(
      feeValues
        .map(item => item.order_fee.audit && item.order_fee.audit.updated_by)
        .filter(Boolean),
    );
    if (userIds.length === 0) return;
    client.clientPrivate
      .query({
        query: usersQuery,
        variables: {
          input: {
            filter: {
              user_filter: {
                user_id: userIds,
                role_not_exist: true,
              },
            },
          },
        },
      })
      .then(({ data: { search_users } }) => {
        if (search_users) {
          setUsers(search_users.user_listing);
        }
      });
  }, [feeValues]);

  const [createFee, { loading }] = useMutation(createFeeMutation, {
    client: client.clientPrivate,
    onCompleted: ({ create_order_fee_default }) => {
      if (!isNull(create_order_fee_default.error)) {
        create_order_fee_default.error.map(item =>
          toast.error(item.description),
        );
      } else {
        setIsActiveModal(false);
        refetch();
      }
    },
  });

  const [updateFee, { loading: loading2 }] = useMutation(updateFeeMutation, {
    client: client.clientPrivate,
    onCompleted: ({ update_order_fee_default }) => {
      if (!isNull(update_order_fee_default.error)) {
        update_order_fee_default.error.map(item =>
          toast.error(item.description),
        );
      } else {
        setIsActiveModal(false);
        refetch();
        setFee(null);
      }
    },
  });

  const [deleteFee] = useMutation(deleteFeeMutation, {
    client: client.clientPrivate,
    onCompleted: ({ delete_order_fee_default }) => {
      if (!isNull(delete_order_fee_default.error)) {
        delete_order_fee_default.error.map(item =>
          toast.error(item.description),
        );
      } else {
        refetch();
      }
    },
  });

  return (
    <React.Fragment>
      <Header setFee={setFee} setIsActiveModal={setIsActiveModal} />
      <Tabs className="tabs is-medium">
        <Tab
          active={activeTab === 'Pickup'}
          onClick={() => {
            setActiveTab('Pickup');
          }}
        >
          Pickup Food Order Fee
        </Tab>
        <Tab
          active={activeTab === 'Delivery'}
          onClick={() => {
            setActiveTab('Delivery');
          }}
        >
          Delivery Food Order Fee
        </Tab>
        <Tab
          active={activeTab === 'Dinein'}
          onClick={() => {
            setActiveTab('Dinein');
          }}
        >
          Dinein Food Order Fee
        </Tab>
        <Tab
          active={activeTab === 'Table booking'}
          onClick={() => {
            setActiveTab('Table booking');
          }}
        >
          Table Booking Food Order Fee
        </Tab>
      </Tabs>
      <Box>
        <Container>
          <CustomTable>
            <Table.Head>
              <Table.Row>
                <Table.Heading>Service Type</Table.Heading>
                <Table.Heading>Fee Name</Table.Heading>
                <Table.Heading>Fee Display Label</Table.Heading>
                <Table.Heading>Sale Method</Table.Heading>
                <Table.Heading>Fee Type</Table.Heading>
                <Table.Heading>Fee</Table.Heading>
                <Table.Heading>Min Fee</Table.Heading>
                <Table.Heading>Max Fee</Table.Heading>
                <Table.Heading>Tax Type</Table.Heading>
                <Table.Heading>Tax Value</Table.Heading>
                <Table.Heading>Is Gateway Fee</Table.Heading>
                <Table.Heading>Is Kravein Fee</Table.Heading>
                <Table.Heading>Applies To</Table.Heading>
                <Table.Heading>Updated By</Table.Heading>
                <Table.Heading>Updated At</Table.Heading>
                <Table.Heading>Action</Table.Heading>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <List
                fees={fees}
                users={users}
                onEdit={value => {
                  setFee(value);
                  setIsActiveModal(true);
                }}
                onDelete={value => {
                  swal('Are you sure you want to delete the fee?', {
                    buttons: ['Cancel', true],
                  }).then(willDelete => {
                    if (!willDelete) return;
                    const wrapper = document.createElement('div');
                    wrapper.style.cssText =
                      'position:relative;display:flex;align-items:center;';
                    const passwordInput = document.createElement('input');
                    passwordInput.type = 'password';
                    passwordInput.className = 'swal-content__input';
                    passwordInput.placeholder = 'XXXXXXX';
                    passwordInput.style.paddingRight = '2.5rem';
                    const toggleBtn = document.createElement('button');
                    toggleBtn.type = 'button';
                    toggleBtn.innerHTML = '&#128065;';
                    toggleBtn.style.cssText =
                      'position:absolute;right:8px;background:none;' +
                      'border:none;cursor:pointer;font-size:1rem;line-height:1;';
                    toggleBtn.addEventListener('click', () => {
                      const isHidden = passwordInput.type === 'password';
                      passwordInput.type = isHidden ? 'text' : 'password';
                      toggleBtn.style.opacity = isHidden ? '1' : '0.4';
                    });
                    wrapper.appendChild(passwordInput);
                    wrapper.appendChild(toggleBtn);
                    swal({
                      title: 'Enter Approval Code',
                      content: wrapper,
                      buttons: ['Cancel', 'Confirm'],
                    }).then(confirmed => {
                      if (!confirmed || !passwordInput.value) return;
                      deleteFee({
                        variables: {
                          input: {
                            user_id: userId,
                            fee_id: value.fee_id,
                            service_type_id: value.service_type_id,
                            approval_code: passwordInput.value,
                          },
                        },
                      });
                    });
                  });
                }}
              />
            </Table.Body>
          </CustomTable>
        </Container>
      </Box>
      {isActiveModal && (
        <AddEditForm
          taxesOptions={taxesOptions}
          fee={fee}
          loading={loading || loading2}
          isActive={isActiveModal}
          onClose={() => setIsActiveModal(false)}
          onSubmit={values => {
            if (fee) {
              updateFee({
                variables: {
                  input: {
                    user_id: userId,
                    service_type_id: values.service_type_id,
                    fee_id: fee.fee_id,
                    value: values.value,
                    min_fee: values.min_fee,
                    max_fee: values.max_fee,
                    display: values.display,
                    is_payment_gateway_fee: values.is_payment_gateway_fee,
                    is_kravein_charge: values.is_kravein_charge,
                    approval_code: values.approval_code,
                    // type: values.type,
                  },
                },
              });
            } else {
              createFee({
                variables: {
                  input: {
                    user_id: userId,
                    service_type_id: values.service_type_id,
                    fee_id: uuidv4(),
                    name: values.name,
                    display: values.display,
                    sale_method: values.sale_method,
                    applies_to: values.applies_to,
                    type: values.type,
                    value: values.value,
                    min_fee: values.min_fee,
                    max_fee: values.max_fee,
                    status: values.status,
                    is_payment_gateway_fee: values.is_payment_gateway_fee,
                    is_kravein_charge: values.is_kravein_charge,
                    approval_code: values.approval_code,
                  },
                },
              });
            }
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Details;
